// utils/pdfStreamFixer.ts
// Repairs FlateDecode content streams in PDFDocuments where the actual bytes
// are raw deflate (missing the RFC 1950 zlib wrapper) that pdf-lib's strict
// FlateStream parser rejects with "Unknown compression method".
// French municipal software commonly produces PDFs with /Filter /FlateDecode
// but raw-deflate bytes instead of proper zlib-wrapped bytes.
//
// Single export:
//   fixRawDeflateStreamsInDoc: pre-embedPdf repair — inflateRaw + deflateZlib
//   to re-wrap raw deflate bytes with a proper RFC 1950 zlib header. When this
//   repair is insufficient, the caller falls back to pdf.js rasterisation
//   (pdfPageRasterizer.ts).
//
// BROWSER-ONLY: uses DecompressionStream / CompressionStream APIs.
//
// Key fix vs earlier version: uses dict.lookup() (which dereferences PDFRef via
// context) rather than dict.get() which returns the raw value — the Filter entry
// is often stored as an indirect ref in French municipal PDFs, causing the old
// get()-based detection to silently skip all streams.

import { PDFDocument, PDFName } from 'pdf-lib';

// Valid zlib (RFC 1950) header: CM field (bits 0-3 of CMF byte) must be 8
// (DEFLATE) and (CMF * 256 + FLG) % 31 must be 0 (header checksum).
function isValidZlibHeader(bytes: Uint8Array): boolean {
  if (bytes.length < 2) return false;
  const cmf = bytes[0];
  const flg = bytes[1];
  return (cmf & 0x0f) === 8 && (cmf * 256 + flg) % 31 === 0;
}

async function drainReadable(readable: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  const chunks: Uint8Array[] = [];
  const reader = readable.getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) chunks.push(value);
  }
  const totalLen = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(totalLen);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}

// Write and drain MUST run concurrently — awaiting write() before starting to
// read causes a backpressure deadlock: the transform stream's internal queue
// fills up and write() never resolves because nobody is consuming the output.
async function inflateRaw(input: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  const writer = ds.writable.getWriter();
  const [, output] = await Promise.all([
    writer.write(input).then(() => writer.close()),
    drainReadable(ds.readable),
  ]);
  return output;
}

async function deflateZlib(input: Uint8Array): Promise<Uint8Array> {
  const cs = new CompressionStream('deflate');
  const writer = cs.writable.getWriter();
  const [, output] = await Promise.all([
    writer.write(input).then(() => writer.close()),
    drainReadable(cs.readable),
  ]);
  return output;
}

// Returns true if the stream has /FlateDecode as its (sole) Filter.
// Uses dict.lookup() — not dict.get() — so that indirect-ref Filter entries
// (common in French municipal PDFs) are properly dereferenced before comparison.
// PDFName uses a module-level pool so identity === comparison is reliable.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function streamHasFlateDecode(stream: any): boolean {
  const dict = stream?.dict;
  if (!dict || typeof dict.lookup !== 'function') return false;

  let filter: unknown;
  try {
    // lookup() calls context.lookup(get(key)) — dereferences any PDFRef.
    filter = dict.lookup(PDFName.of('Filter'));
  } catch {
    return false;
  }
  if (!filter) return false;

  const FlateDecode = PDFName.of('FlateDecode');

  // Single name: /FlateDecode
  if (filter === FlateDecode) return true;

  // Single-element array: [/FlateDecode]
  if (typeof (filter as any).size === 'function' && (filter as any).size() === 1) {
    const first = (filter as any).get(0);
    if (first === FlateDecode) return true;
    // first might still be a PDFRef — resolve via dict's context
    try {
      const resolved = dict.context?.lookup?.(first);
      return resolved === FlateDecode;
    } catch {
      return false;
    }
  }

  return false;
}

// Attempts to repair a single PDFRawStream instance (cast as any).
// Returns true if the stream was repaired in-place, false if unchanged.
async function tryRepairStream(streamObj: unknown): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = streamObj as any;
  if (!s?.dict || !(s.contents instanceof Uint8Array)) return false;
  if (!streamHasFlateDecode(s)) return false;
  if (isValidZlibHeader(s.contents as Uint8Array)) return false; // Already valid zlib

  const bytes = s.contents as Uint8Array;

  // Strategy 1: bytes are raw deflate (no zlib wrapper).
  // Decompress with DecompressionStream('deflate-raw'), re-compress with
  // CompressionStream('deflate') which emits proper RFC 1950 zlib output.
  try {
    const decompressed = await inflateRaw(bytes);
    const recompressed = await deflateZlib(decompressed);
    s.contents = recompressed;
    s.dict.set(PDFName.of('Length'), s.dict.context.obj(recompressed.length));
    console.log('[pdfStreamFixer] Strategy 1: re-encoded raw-deflate stream as valid zlib');
    return true;
  } catch {
    // Not valid raw deflate — return false so the caller falls back to pdf.js rasterisation.
    return false;
  }
}

/**
 * Walks all content streams in every page of `srcDoc` and repairs any stream
 * that declares /FlateDecode but has non-standard bytes. Mutates the document
 * in-place. Call before embedPdf or copyPages.
 * Returns the number of streams that were successfully repaired.
 */
export async function fixRawDeflateStreamsInDoc(srcDoc: PDFDocument): Promise<number> {
  let fixedCount = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const context = (srcDoc as any).context as any;

  for (const pageIndex of srcDoc.getPageIndices()) {
    const page = srcDoc.getPage(pageIndex);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const node = page.node as any;
    const contentsEntry = node.get(PDFName.of('Contents'));
    if (!contentsEntry) continue;

    // Dereference the Contents entry (PDFRef → PDFStream or PDFArray).
    const contents = context.lookup(contentsEntry);
    if (!contents) continue;

    // Contents can be a PDFArray of stream refs, or directly a PDFStream.
    const streamObjs: unknown[] = [];
    if (typeof contents.size === 'function') {
      // PDFArray: each element is a ref to a content stream.
      for (let i = 0; i < contents.size(); i++) {
        const item = context.lookup(contents.get(i));
        if (item) streamObjs.push(item);
      }
    } else {
      // Single PDFStream directly (shouldn't happen post-normalize, but handle it).
      streamObjs.push(contents);
    }

    for (const streamObj of streamObjs) {
      if (await tryRepairStream(streamObj)) {
        fixedCount++;
        console.log(`[pdfStreamFixer] Repaired content stream on page ${pageIndex + 1}`);
      }
    }
  }

  return fixedCount;
}
